import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { GetOrCreateError, GetOrCreateResponse, ResidentialService } from '../../../core/services/residential.service';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, switchMap, tap } from 'rxjs/operators';
import { EMPTY, Observable, of, Subscription, throwError } from 'rxjs';
import { FastAPIAddressExtended, FastAPIResponse, FastAPIService } from '../fastapi.service';
import { ToastrService } from 'ngx-toastr';

enum AddressesSource {
  OSM = 'OSM',
  Google = 'Google',
  GC = 'GC',
  None = 'None'
}

@Component({
  selector: 'ui-address-search',
  templateUrl: './ui-address-search.component.html',
  styleUrls: ['./ui-address-search.component.scss']
})
export class UiAddressSearchComponent implements OnInit, OnDestroy {
  readonly PARTIAL_SEARCH_ERROR_CODE = 406;
  readonly SEARCH_MIN_LENGTH = 6;

  @Input() form: FormGroup;
  @Input() preserveRawAddress = false;
  @Output() setBuildingsFromAddress = new EventEmitter();
  @Output() setUnitsFromAddress = new EventEmitter();
  @Output() setAccountId = new EventEmitter();
  @Output() setCommunity = new EventEmitter();

  addresses = [];
  subscription: Subscription = new Subscription();
  loadingAddress = false;
  showAddressHint = false;

  continueEnteringHint = false;

  private addressesSource: AddressesSource = AddressesSource.None;

  selectedAddress: string;

  constructor(private residentialService: ResidentialService,
              private fastAPIService: FastAPIService,
              private toastrService: ToastrService) {
  }

  ngOnInit() {
    this.onAddressChanges();
  }

  onAddressChanges() {
    this.addresses = [];

    this.addressesSource = AddressesSource.None;

    const sub = this.form.get('address').valueChanges
      .pipe(
        debounceTime(800),
        distinctUntilChanged((prev, curr)=> (curr === prev || curr === this.selectedAddress)),
        switchMap(() => of(this.form.get('address').value)),
        filter(search => search && typeof search === 'string' && search.length > 0),
        filter((term: string) => {
          const isValid = term.length >= this.SEARCH_MIN_LENGTH;
          this.continueEnteringHint = !isValid;
          return isValid;
        }),
      ).subscribe(search => {
        this.loadingAddress = true;

        this.lookupAddresses(search).pipe(
          tap(() => {
            this.showAddressHint = false;
            this.continueEnteringHint = false;
          }),
          finalize(() => {
            this.loadingAddress = false;
          }),
          catchError((err) => {
            this.addresses = [];
            this.showAddressHint = false;
            this.continueEnteringHint = err?.originError?.status === this.PARTIAL_SEARCH_ERROR_CODE;
            return EMPTY;
          })
        ).subscribe((res: FastAPIResponse) => this.setAvailableAddresses(res));
      });

    this.subscription.add(sub);
  }

  selectAddress(address) {
    this.selectedAddress = address;

    this.form.get('address').setValue(address.full_str, {emitEvent: false})
    this.getOrCreate(address);
  }

  validateStates(community) {
    const primaryAddress = community && community.primary_address ? community.primary_address : null
    const state = primaryAddress && primaryAddress.state ? primaryAddress.state : null;
    if (state === 'AK') {
      this.form.get('address').setErrors({invalidState: true});
    }
  }

  setBuildings(community) {
    const buildings = community && community.buildings ? community.buildings : [];
    this.setBuildingsFromAddress.emit(buildings);
  }

  setUnits(community) {
    const units = community && community.units ? community.units : [];
    this.setUnitsFromAddress.emit(units);
  }

  displayFnInsurable(address) {
    return address && address.full_str ? address.full_str :
      address ? address : '';
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private getOrCreate(address): void {
    this.form.get('insurable_id').setValue(null);
    this.form.get('address').setErrors(null);

    const form = this.form.value;

    let request: Observable<GetOrCreateResponse>;

    if (this.addressesSource === AddressesSource.OSM) {
      request = this.getOrCreateInsurable(form);
    } else if (this.addressesSource === AddressesSource.Google) {
      request = this.sendPlace(address?.place_id)
        .pipe(
          tap(res => this.setFormValuesBasedOnGoogleSearch(res)),
          switchMap((res) => this.getOrCreateInsurable(res))
        );
    }

    if (request) {
      request.subscribe((result: GetOrCreateResponse) => this.handleGetOrCreateInsurableResponse(result));
    } else {
      console.error('[UI Address Source] Address data source wasn\'t detected');
    }
  }

  private setFormValuesBasedOnGoogleSearch(res: FastAPIAddressExtended): void {
    const county = this.form.get('county');
    const neighborhood = this.form.get('neighborhood');

    this.form.get('address')?.patchValue(res?.full_str, {emitEvent: false});

    if(county && res?.county) {
      county?.patchValue(res?.county, {emitEvent: false});
    }

    if(neighborhood && res?.neighborhood) {
      county?.patchValue(res?.neighborhood, {emitEvent: false});
    }
  }

  private getOrCreateInsurable(form: any): Observable<GetOrCreateResponse> {
    return this.residentialService.getOrCreateInsurable(form).pipe(
      catchError((err: GetOrCreateError) => {
        const message = `${err?.message}. ${err.payload.join(', ')}`;
        this.toastrService.error(message);
        return throwError(err);
      })
    );
  }

  private lookupAddresses(searchString: string): Observable<FastAPIResponse> {
    return this.fastAPIService.lookup(searchString);
  }

  private sendPlace(placeId: string): Observable<FastAPIAddressExtended> {
    return this.fastAPIService.sendPlace(placeId);
  }

  private setAvailableAddresses(res: FastAPIResponse): void {
    let addresses = [];

    const isOSMAvailable = res?.osm && res?.osm?.length > 0;
    const isGoogleAvailable = res?.google && res?.google?.length > 0;

    if (isOSMAvailable) {
      this.addressesSource = AddressesSource.OSM;

      addresses = res?.osm;
    }

    if (isGoogleAvailable && addresses?.length === 0) {
      addresses = res?.google;

      this.addressesSource = AddressesSource.Google;

      addresses.forEach(address => {
        address.full_str = address?.description;
      });
    }

    this.addresses = addresses;

    if (this.addresses.length === 0) {
      this.loadingAddress = true;
    }
  }

  private handleGetOrCreateInsurableResponse(result: GetOrCreateResponse): void {
    const community = result && result.results && result.results[0] ? result.results[0] : null;
    if (community) {
      this.validateStates(community);
      this.setCommunity.emit(community);
    }
    if (community && community.id) {
      this.form.get('insurable_id').setValue(community.id);

      if (this.preserveRawAddress && community?.primary_address) {
        this.form.get('raw_address').setValue(community.primary_address);
      }
    }
    const accountId = community && community.account_id ? community.account_id : null;
    if (accountId) {
      this.setAccountId.emit(accountId);
    }
    this.setBuildings(community);
    this.setUnits(community);
  }
}
